import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Share2, Facebook, Instagram, Download, Sparkles } from "lucide-react";
import html2canvas from "html2canvas";

// Curated affirmations for parents and families
const affirmations = [
  "Your calm is worth protecting.",
  "Progress, not perfection, is the goal.",
  "You are doing better than you think.",
  "Every small step forward matters.",
  "Your child's pace is their own journey.",
  "Connection before correction.",
  "You are the expert on your child.",
  "Rest is part of the process.",
  "Setbacks are setups for comebacks.",
  "Your presence is their safe space.",
  "Patience with yourself is patience with your child.",
  "You don't have to have all the answers today.",
  "Growth happens in the quiet moments too.",
  "Your love is already making a difference.",
  "Breathe. You've got this.",
  "Celebrate the small victories.",
  "Your family's timeline is unique.",
  "Compassion starts with yourself.",
  "One day at a time, one moment at a time.",
  "You are not alone in this journey.",
  "Trust the process, trust yourself.",
  "Every effort you make matters.",
  "Your child feels your love, even on hard days.",
  "Healing is not linear, and that's okay.",
  "You are building something beautiful.",
  "Today's struggle is tomorrow's strength.",
  "Be gentle with yourself today.",
  "Your dedication speaks louder than words.",
  "It's okay to ask for help.",
  "You are exactly the parent your child needs.",
];

// Get today's affirmation based on date
const getTodaysAffirmation = () => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  return affirmations[dayOfYear % affirmations.length];
};

// Snapchat icon component
const SnapchatIcon = ({ size = 24, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12.206 1c.914 0 3.1.53 4.166 3.477.397 1.094.27 3.226.167 4.64l-.006.074c-.015.196-.03.387-.041.563a.453.453 0 0 0 .213.415c.158.1.332.15.51.15.166 0 .335-.043.492-.13.15-.084.432-.19.724-.19.17 0 .51.035.77.283.225.215.336.52.321.882-.02.483-.317.907-.883 1.26-.16.1-.333.185-.5.267-.39.19-.76.37-.925.687-.096.185-.108.39-.107.51.002.05.004.094.004.134 0 .033 0 .067-.002.099-.082.7-.526 1.406-1.32 2.1-.754.66-1.72 1.23-2.622 1.553-.338.121-.656.295-.947.455-.475.26-.912.5-1.42.5-.072 0-.145-.004-.216-.012-.1.008-.2.012-.297.012-.51 0-.945-.24-1.422-.5-.29-.16-.608-.334-.946-.455-.902-.324-1.868-.894-2.622-1.553-.794-.694-1.238-1.4-1.32-2.1a1.247 1.247 0 0 1-.002-.1c0-.04.002-.083.004-.133.001-.12-.01-.325-.107-.51-.165-.317-.535-.497-.925-.687-.167-.082-.34-.166-.5-.267-.566-.353-.863-.777-.883-1.26-.015-.362.096-.667.32-.882.262-.248.602-.283.772-.283.292 0 .573.106.724.19.157.087.326.13.492.13.178 0 .352-.05.51-.15a.453.453 0 0 0 .213-.415 23.2 23.2 0 0 1-.041-.563l-.006-.074c-.103-1.414-.23-3.546.167-4.64C6.106 1.53 8.29 1 9.204 1h2.002z"/>
  </svg>
);

export const AffirmationOfTheDay = ({ variant = "section" }) => {
  const [showModal, setShowModal] = useState(false);
  const [shareCount, setShareCount] = useState(Math.floor(Math.random() * 10) + 1);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const affirmationRef = useRef(null);
  const affirmation = getTodaysAffirmation();

  // Generate shareable image
  const generateShareImage = async (format = "post") => {
    setIsGeneratingImage(true);
    
    // Create a temporary div for the share image
    const shareDiv = document.createElement("div");
    shareDiv.style.cssText = `
      width: ${format === "story" ? "1080px" : "1200px"};
      height: ${format === "story" ? "1920px" : "630px"};
      background: linear-gradient(135deg, #1E293B 0%, #334155 50%, #1E293B 100%);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 60px;
      position: fixed;
      left: -9999px;
      font-family: system-ui, -apple-system, sans-serif;
    `;
    
    shareDiv.innerHTML = `
      <div style="text-align: center;">
        <p style="color: #C9A962; font-size: 24px; margin-bottom: 30px; letter-spacing: 2px;">A GENTLE NOTE FOR TODAY</p>
        <p style="color: white; font-size: ${format === "story" ? "64px" : "48px"}; font-weight: 600; line-height: 1.4; margin-bottom: 40px;">"${affirmation}"</p>
        <div style="display: flex; align-items: center; justify-content: center; gap: 12px; margin-top: 40px;">
          <img src="https://customer-assets-rejwkqb3.emergentagent.net/job_modern-minds-landing/artifacts/pe71uytn_Modern%20Minds%20Logo.jpeg" style="width: 60px; height: 60px; border-radius: 50%;" />
          <div style="text-align: left;">
            <p style="color: white; font-size: 20px; font-weight: 600; margin: 0;">Modern Minds</p>
            <p style="color: #94A3B8; font-size: 16px; margin: 0;">www.modernminds.co.in</p>
          </div>
        </div>
      </div>
    `;
    
    document.body.appendChild(shareDiv);
    
    try {
      const canvas = await html2canvas(shareDiv, {
        scale: 2,
        useCORS: true,
        allowTaint: true,
      });
      
      const dataUrl = canvas.toDataURL("image/png");
      document.body.removeChild(shareDiv);
      setIsGeneratingImage(false);
      return dataUrl;
    } catch (error) {
      console.error("Error generating image:", error);
      document.body.removeChild(shareDiv);
      setIsGeneratingImage(false);
      return null;
    }
  };

  // Download image for manual sharing
  const downloadImage = async (format) => {
    const dataUrl = await generateShareImage(format);
    if (dataUrl) {
      const link = document.createElement("a");
      link.download = `modern-minds-affirmation-${format}.png`;
      link.href = dataUrl;
      link.click();
      setShareCount(prev => prev + 1);
    }
  };

  // Share to Facebook
  const shareToFacebook = () => {
    const url = encodeURIComponent("https://www.modernminds.co.in");
    const quote = encodeURIComponent(`A gentle note for today — "${affirmation}" — From Modern Minds`);
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${quote}`, "_blank", "width=600,height=400");
    setShareCount(prev => prev + 1);
  };

  // Share to Instagram (download image for manual posting)
  const shareToInstagram = async (format) => {
    await downloadImage(format);
    alert("Image downloaded! Open Instagram and share it as a " + (format === "story" ? "Story" : "Post") + ".");
  };

  // Share to Snapchat (download image for manual posting)
  const shareToSnapchat = async (format) => {
    await downloadImage(format);
    alert("Image downloaded! Open Snapchat and share it as a " + (format === "story" ? "Story" : "Snap") + ".");
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    const text = `A gentle note for today — "${affirmation}" — From Modern Minds · www.modernminds.co.in`;
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  const AffirmationContent = ({ isModal = false }) => (
    <motion.div
      ref={!isModal ? affirmationRef : null}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={`glass-card p-6 sm:p-8 text-center ${isModal ? "" : "max-w-2xl mx-auto"}`}
    >
      <div className="flex items-center justify-center gap-2 mb-4">
        <Sparkles size={16} className="text-[#C9A962]" />
        <span className="font-body text-xs text-[#C9A962] uppercase tracking-widest">
          A gentle note for today
        </span>
        <Sparkles size={16} className="text-[#C9A962]" />
      </div>
      
      <p className="font-heading text-xl sm:text-2xl lg:text-3xl text-white font-medium leading-relaxed mb-6">
        "{affirmation}"
      </p>
      
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <button
          onClick={() => setShowShareOptions(!showShareOptions)}
          className="inline-flex items-center gap-2 btn-outline rounded-full px-5 py-2 font-body text-sm"
        >
          <Share2 size={16} />
          Pass it on
        </button>
        <span className="font-body text-xs text-[#778DA9]">
          Shared {shareCount} time{shareCount !== 1 ? "s" : ""} today · Join in
        </span>
      </div>

      {/* Share Options */}
      <AnimatePresence>
        {showShareOptions && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-6 pt-6 border-t border-white/10"
          >
            <p className="font-body text-xs text-[#94A3B8] mb-4">Choose platform & format:</p>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Facebook */}
              <div className="glass-card p-4">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Facebook size={20} className="text-[#1877F2]" />
                  <span className="font-body text-sm text-white">Facebook</span>
                </div>
                <button
                  onClick={shareToFacebook}
                  className="w-full bg-[#1877F2]/20 hover:bg-[#1877F2]/30 text-[#1877F2] rounded px-3 py-2 font-body text-xs transition-colors"
                >
                  Share Post
                </button>
              </div>

              {/* Instagram */}
              <div className="glass-card p-4">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <Instagram size={20} className="text-[#E4405F]" />
                  <span className="font-body text-sm text-white">Instagram</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => shareToInstagram("post")}
                    disabled={isGeneratingImage}
                    className="flex-1 bg-[#E4405F]/20 hover:bg-[#E4405F]/30 text-[#E4405F] rounded px-2 py-2 font-body text-xs transition-colors disabled:opacity-50"
                  >
                    Post
                  </button>
                  <button
                    onClick={() => shareToInstagram("story")}
                    disabled={isGeneratingImage}
                    className="flex-1 bg-[#E4405F]/20 hover:bg-[#E4405F]/30 text-[#E4405F] rounded px-2 py-2 font-body text-xs transition-colors disabled:opacity-50"
                  >
                    Story
                  </button>
                </div>
              </div>

              {/* Snapchat */}
              <div className="glass-card p-4">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <SnapchatIcon size={20} className="text-[#FFFC00]" />
                  <span className="font-body text-sm text-white">Snapchat</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => shareToSnapchat("post")}
                    disabled={isGeneratingImage}
                    className="flex-1 bg-[#FFFC00]/20 hover:bg-[#FFFC00]/30 text-[#FFFC00] rounded px-2 py-2 font-body text-xs transition-colors disabled:opacity-50"
                  >
                    Snap
                  </button>
                  <button
                    onClick={() => shareToSnapchat("story")}
                    disabled={isGeneratingImage}
                    className="flex-1 bg-[#FFFC00]/20 hover:bg-[#FFFC00]/30 text-[#FFFC00] rounded px-2 py-2 font-body text-xs transition-colors disabled:opacity-50"
                  >
                    Story
                  </button>
                </div>
              </div>
            </div>

            {/* Download Options */}
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => downloadImage("post")}
                disabled={isGeneratingImage}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded px-4 py-2 font-body text-xs transition-colors disabled:opacity-50"
              >
                <Download size={14} />
                Download Post (1200x630)
              </button>
              <button
                onClick={() => downloadImage("story")}
                disabled={isGeneratingImage}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded px-4 py-2 font-body text-xs transition-colors disabled:opacity-50"
              >
                <Download size={14} />
                Download Story (1080x1920)
              </button>
              <button
                onClick={copyToClipboard}
                className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white rounded px-4 py-2 font-body text-xs transition-colors"
              >
                Copy Text
              </button>
            </div>

            {isGeneratingImage && (
              <p className="font-body text-xs text-[#C9A962] mt-3">Generating image...</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );

  // Render as section on homepage
  if (variant === "section") {
    return (
      <section className="py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-6">
          <AffirmationContent />
        </div>
      </section>
    );
  }

  // Render as modal trigger + modal
  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setShowModal(true)}
        className="fixed bottom-6 right-6 z-40 bg-gradient-to-r from-[#C9A962] to-[#7C9885] text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all hover:scale-105"
        aria-label="Affirmation of the Day"
      >
        <Sparkles size={24} />
      </button>

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative w-full max-w-lg"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setShowModal(false)}
                className="absolute -top-12 right-0 text-white/70 hover:text-white transition-colors"
              >
                <X size={24} />
              </button>
              <AffirmationContent isModal />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AffirmationOfTheDay;
